import { Carousel } from './carousel';

/**
 * IntroCarousel is carousel component displayed in full width
 */
export class IntroCarousel extends Carousel {
  /**
   * css selector of carousel container
   * @static
   */
  static selector = '.js-intro-carousel';

  /**
   * Checks if container node is present in the dom
   * @function isOnPage
   * @returns {Boolean}
   * @static
   */
  static isOnPage() {
    return document.querySelector(IntroCarousel.selector);
  }

  constructor() {
    super();
    this.carousel = new Map();
    super.init(this, IntroCarousel.selector);
  }

  /**
   * Synchronizes the tabindex of slide links based on the presence of nested interactive elements.
   * @param {Node} carousel - DOM element of the carousel.
   */
  syncSlideTabindex = (carousel) => {
    $(carousel).find('.intro-carousel-slide .intro-carousel-link').each((index, el) => {
      const $activeSlide = $(el);
      const $slide = $activeSlide.closest('.intro-carousel-slide');
      const hasButton = $slide.find('.intro-carousel-btn').length > 0;
      const hasVideo = $slide.find('.video-popup-image-wrapper-button').length > 0;

      $activeSlide.attr('tabindex', (hasButton || hasVideo) ? '-1' : '0');
      $slide.find('.svg-icon').attr({
        'tabindex': '-1',
        'aria-hidden' : 'true'
      });
    });
  }

  /**
   * Adds event listener for focus to activate the slide and prevent layout shift.
   * @param {Swiper} swiper - Swiper instance.
   * @param {Node} $carouselElement - DOM element of the carousel.
   */
  enableFocusSlideActivation(swiper, $carouselElement) {
    $carouselElement.addEventListener('focusin', (event) => {
      if (swiper.autoplay && swiper.autoplay.running) {
        const $playPauseBtn = $($carouselElement).find('.js-play-pause-box-btn');

        // Stop autoplay and sync UI state (icons/labels) when the carousel receives focus.
        if ($playPauseBtn.length > 0) {
          $playPauseBtn.trigger('click');
        } else {
          swiper.autoplay.stop();
        }
      }

      const slideElement = event.target.closest('.swiper-slide');

      if (slideElement && swiper && $carouselElement.contains(slideElement)) {
        const slideIndex = Array.from(swiper.slides).indexOf(slideElement);

        if (slideIndex > -1 && swiper.activeIndex !== slideIndex) {
          swiper.slideTo(slideIndex, 0);
        }

        // Reset native scroll position to prevent layout shifts caused by browser focus behavior.
        // This ensures that the pagination and other controls remain visible.
        setTimeout(() => {
          $carouselElement.scrollLeft = 0;
          $carouselElement.scrollTop = 0;
        }, 0);
      }
    }, { capture: true });
  }

  /**
   * Generate config depending of the number of the slides in carousel by extending the default config
   * @param {Node} $carousel - Carousel DOM Node
   * @returns {Object} extended config
   */
  generateConfig($carousel) {
    if (super.hasSingleSlide($carousel)) {
      return super.extendConfig({
        pagination: false,
        loop: false,
        autoplay: false
      });
    }

    return super.extendConfig({
      autoplay: super.toggleAutoplay($carousel),
      loop: false,
      pagination: {
        el: $carousel.querySelector('.js-carousel-pagination-intro'),
        clickable: true,
        type: 'bullets',
        renderBullet: (index, className) => `<button class="${className}" aria-current="false" data-index="${index + 1}"></button>`
      },
      navigation: {
        nextEl: $carousel.querySelector('.swiper-button-next'),
        prevEl: $carousel.querySelector('.swiper-button-prev')
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      on: {
        init: (swiper) => {
          const $pagination = $(swiper.pagination?.el);
          const $controlsNav = $(swiper.el).find('.carousel-controls');
          const baseLabel = $controlsNav.attr('data-aria-label') || 'Slide';

          if ($pagination.length > 0) {
            $pagination.find('.swiper-pagination-bullet-active').attr('aria-current', 'true');
            $pagination.find('.swiper-pagination-bullet').each((index, el) => {
              const $bullet = $(el);
              const bulletIndex = $bullet.attr('data-index') || (index + 1);

              if (!$bullet.attr('aria-label')) {
                $bullet.attr('aria-label', `${baseLabel} ${bulletIndex}`);
              }
            });
          }

          this.syncSlideTabindex(swiper.el);
          this.enableFocusSlideActivation(swiper, swiper.el);
        },

        slideChange: (swiper) => {
          const $pagination = $(swiper.pagination?.el);

          if ($pagination.length > 0) {
            $pagination.find('.swiper-pagination-bullet').each((index, el) => {
              const $bullet = $(el);
              const isActive = $bullet.hasClass('swiper-pagination-bullet-active');
              $bullet.attr('aria-current', isActive ? 'true' : 'false');
            });
          }
        }
      }
    });
  }
}